import { useLocalSearchParams, Link } from "expo-router"
import { View, ActivityIndicator, Pressable } from "react-native"
import { Header } from "components/Header"
import { trpc } from "utils/trpc"
import { ar } from "i18n/ar"
import MIcon from "@react-native-vector-icons/material-design-icons"
import { Show, useObservable } from "@legendapp/state/react"
import { ScrollView } from "@src/components/list"
import { Controller, useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { addEmployeeBalanceInput, employeePayment } from "@white-labels/input/src/m5azen"
import { z } from "@white-labels/input"
import { Button, ModalWrapper, Text, TextInput } from "components/core"
import { PriceInput } from "@src/components/price-input"
import { sharedStyles, priceFromDBFormat } from "@src/utils"

export default function EmployeeDetails() {
  const { id } = useLocalSearchParams<{ id: string }>()
  const addBalanceModal$ = useObservable(false)
  const addPaymentModal$ = useObservable(false)
  const employeeId = Number.parseInt(id as string, 10)
  const employee = trpc.employees.get.useQuery({ id: employeeId })

  const header = (
    <Header
      title={ar.employeeDetails}
      right={
        <Link href={`/dashboard/employee/${employeeId}/edit`} asChild>
          <Pressable className="px-2 h-full justify-center">
            <MIcon name="pencil" size={24} color="#3B82F6" />
          </Pressable>
        </Link>
      }
    />
  )

  if (employee.isLoading) {
    return (
      <>
        {header}
        <ActivityIndicator size="large" className="mt-8 mx-auto" />
      </>
    )
  }

  return (
    <>
      {header}
      <ScrollView contentContainerStyle={sharedStyles.contentContainer} onRefresh={employee.refetch}>
        <View className="bg-white rounded shadow-sm p-6 mb-4">
          <View className="items-center mb-6">
            <View className="w-20 h-20 bg-gray-200 rounded-full items-center justify-center mb-4">
              <MIcon name="account" size={40} color="#6B7280" />
            </View>
            <Text className="text-2xl font-bold text-gray-800">{employee.data?.name ?? employee.data?.username}</Text>
            <Text className="text-lg text-gray-600 mt-1">{employee.data?.role ? ar[employee.data?.role] : ""}</Text>
          </View>

          <View className="space-y-4">
            <View className="flex-row justify-between items-center py-3 border-b border-gray-200">
              <Text className="text-gray-600 font-medium">{ar.username}</Text>
              <Text className="text-gray-800">{employee.data?.username}</Text>
            </View>

            <View className="flex-row justify-between items-center py-3 border-b border-gray-200">
              <Text className="text-gray-600 font-medium">{ar.role}</Text>
              <Text className="text-gray-800">{employee.data?.role ? ar[employee.data?.role] : ""}</Text>
            </View>

            {/* <View className="flex-row justify-between items-center py-3 border-b border-gray-200 mb-4">
              <Text className="text-gray-600 font-medium">{ar.theSalary}</Text>
              <Text className="text-gray-800">{employee.data?.salary}</Text>
            </View> */}

            <View className="flex-row justify-between items-center py-3 border-b border-gray-200 mb-4">
              <Text className="text-gray-600 font-medium">{ar.balance}</Text>
              <Text className="text-gray-800">{priceFromDBFormat(employee.data?.balance ?? 0).toFixed(2)}</Text>
            </View>

            <View className="flex-row justify-center gap-x-2">
              <Pressable
                className="bg-blue-500 px-4 py-2 rounded active:opacity-70"
                onPress={() => {
                  addBalanceModal$.set(true)
                }}
              >
                <Text className="text-white font-medium text-left">{ar.addBalance}</Text>
              </Pressable>

              <Pressable
                className="bg-blue-500 px-4 py-2 rounded active:opacity-70"
                onPress={() => {
                  addPaymentModal$.set(true)
                }}
              >
                <Text className="text-white font-medium text-left">{ar.payment}</Text>
              </Pressable>
              <Link href={`/dashboard/employee/${employeeId}/payment`} asChild>
                <Pressable className="bg-blue-500 px-4 py-2 rounded active:opacity-70">
                  <Text className="text-white font-medium text-left">{ar.paymentHistory}</Text>
                </Pressable>
              </Link>
            </View>
          </View>
        </View>
      </ScrollView>

      <Show if={addBalanceModal$}>
        <BalanceModal hide={() => addBalanceModal$.set(false)} employeeId={employeeId} />
      </Show>

      <Show if={addPaymentModal$}>
        <PaymentModal hide={() => addPaymentModal$.set(false)} employeeId={employeeId} />
      </Show>
    </>
  )
}

const balanceSchema = z.omit(addEmployeeBalanceInput, { employeeId: true })
function BalanceModal({ hide, employeeId }: { hide: () => void; employeeId: number }) {
  const addBalance = trpc.employees.addBalance.useMutation()
  const utils = trpc.useUtils()

  const { control, handleSubmit, formState } = useForm({
    resolver: zodResolver(balanceSchema),
  })

  const { isValid, isDirty } = formState
  const loading = addBalance.isPending
  const disabled = !isValid || !isDirty || loading

  const onSubmit = handleSubmit(async (data) => {
    await addBalance.mutateAsync({ ...data, employeeId })
    utils.employees.get.invalidate()
    hide()
  })

  return (
    <ModalWrapper onClose={hide}>
      <View className="flex-row items-center justify-between mb-2">
        <Text className="text-lg font-bold text-gray-800">{ar.addBalance}</Text>
        <Pressable onPress={hide} className="p-2">
          <MIcon name="close" size={24} color="#6B7280" />
        </Pressable>
      </View>

      <Text className="text-gray-600 font-medium mb-2">{ar.amount}</Text>
      <Controller
        control={control}
        name="amount"
        render={({ field }) => {
          return <PriceInput className="mb-2" autoFocus value={field.value as number | undefined} onValueChange={field.onChange} />
        }}
      />

      <Text className="text-gray-600 font-medium mb-2">{ar.notes}</Text>
      <Controller
        control={control}
        name="notes"
        render={({ field }) => {
          return <TextInput className="mb-2" multiline value={field.value} onChangeText={field.onChange} />
        }}
      />

      <Pressable className="bg-blue-500 py-3 rounded active:opacity-70 disabled:opacity-50" onPress={onSubmit} disabled={disabled}>
        <Text className="text-white font-medium text-center">{ar.confirm}</Text>
      </Pressable>
    </ModalWrapper>
  )
}

const paymentSchema = z.omit(employeePayment, { employeeId: true })
function PaymentModal({ hide, employeeId }: { hide: () => void; employeeId: number }) {
  const addPayment = trpc.employees.addPayment.useMutation()
  const utils = trpc.useUtils()

  const { control, handleSubmit, formState } = useForm({
    resolver: zodResolver(paymentSchema),
  })

  const { isValid, isDirty } = formState
  const loading = addPayment.isPending
  const disabled = !isValid || !isDirty || loading

  const onSubmit = handleSubmit(async (data) => {
    await addPayment.mutateAsync({ ...data, employeeId })
    utils.balance.invalidate()
    utils.balance.track.invalidate()
    utils.employees.get.invalidate()
    utils.balance.invalidate()
    utils.employees.paymentHistory.invalidate({ id: employeeId }, { refetchType: "all" })
    hide()
  })

  return (
    <ModalWrapper onClose={hide}>
      <View className="flex-row items-center justify-between">
        <Text className="text-lg font-bold text-gray-800">{ar.addPayment}</Text>
        <Pressable onPress={hide} className="p-2">
          <MIcon name="close" size={24} color="#6B7280" />
        </Pressable>
      </View>
      <Text className="mb-2">{ar.amount}</Text>
      <Controller
        control={control}
        name="amount"
        render={({ field }) => {
          return <PriceInput autoFocus className="mb-2" value={field.value as number | undefined} onValueChange={field.onChange} />
        }}
      />

      <Text className="mb-2">{ar.notes}</Text>
      <Controller
        control={control}
        name="notes"
        render={({ field }) => {
          return <TextInput multiline className="mb-2" value={field.value} onChangeText={field.onChange} />
        }}
      />

      <Button onPress={onSubmit} disabled={disabled} loading={addPayment.isPending}>
        {ar.confirm}
      </Button>
    </ModalWrapper>
  )
}
